import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { createV2RuntimeCoordinator } from '@shared/Resources/Monitor/v2Monitors/runtime/coordinator';
import { V2PauseButton } from './styles';

const V2MonitorHost = forwardRef(({
  definition,
  monitor,
  context,
  exercisePaused,
  onPause,
}, ref) => {
  const { createController, defaultConfig, RuntimeComponent } = definition;
  const [config, setConfig] = useState({ ...defaultConfig, ...monitor.config });
  const coordinatorRef = useRef(null);

  if (!coordinatorRef.current) {
    coordinatorRef.current = createV2RuntimeCoordinator({
      controller: createController(config),
      monitor: {
        type: monitor.type,
        version: definition.version || 2,
        config,
      },
    });
  }

  useImperativeHandle(ref, () => ({
    finishRepetition: coordinatorRef.current.finishRepetition,
  }), []);

  useEffect(() => {
    coordinatorRef.current.sync(context);
  }, [context]);

  useEffect(() => {
    if (exercisePaused) {
      coordinatorRef.current.pause();
    } else if (context.active) {
      coordinatorRef.current.resume();
    }
  }, [context.active, exercisePaused]);

  useEffect(() => () => coordinatorRef.current.cancel(), []);

  const updateConfig = (nextConfig) => {
    coordinatorRef.current.updateConfig(nextConfig);
    setConfig(nextConfig);
  };

  return (
    <>
      <RuntimeComponent config={config} onConfigChange={updateConfig} />
      <V2PauseButton onPause={onPause} />
    </>
  );
});

V2MonitorHost.displayName = 'V2MonitorHost';

V2MonitorHost.propTypes = {
  definition: PropTypes.shape({
    createController: PropTypes.func.isRequired,
    defaultConfig: PropTypes.object,
    RuntimeComponent: PropTypes.any.isRequired,
    version: PropTypes.number.isRequired,
  }).isRequired,
  monitor: PropTypes.shape({
    type: PropTypes.string.isRequired,
    config: PropTypes.object,
  }),
  context: PropTypes.shape({
    active: PropTypes.bool.isRequired,
    activityId: PropTypes.string,
    productionIndex: PropTypes.number.isRequired,
    repetitionIndex: PropTypes.number.isRequired,
    repetitionCount: PropTypes.number.isRequired,
    stimulus: PropTypes.any,
  }).isRequired,
  exercisePaused: PropTypes.bool.isRequired,
  onPause: PropTypes.func.isRequired,
};

export default V2MonitorHost;
